import React, { useEffect, useState } from "react";
import styles from "./OrderInfoPage.module.scss";
import className from "classnames/bind";
import useCheckoutProductStore from "../../stores/CheckoutProduct/useCheckoutProductStore";
import { useLanguageStore } from "../../stores/Language/useLanguageStore";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import useAuthStore from "../../stores/Auth/AuthStore";
import useTranslations from "../../hooks/useTranslations";
import useGHNStore from "../../stores/GHN/useGHNStore";
import { calculateShippingCost } from "../../services/GHN/GHNService";

// Import các component con
import {
  PersonalInfoForm,
  ShippingDetailsForm,
  PaymentMethodSelector,
  OrderSummary,
  SuccessModal,
} from "./components";

// Import custom hook
import { useShippingLocation } from "../../hooks/useShippingLocation";
import { useOrderSubmission } from "../../hooks/useOrderSubmission";

const cx = className.bind(styles);

const OrderInfoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    items: checkoutProducts,
    subtotal,
    createOrderFromCheckout,
    updateOrderWithPaymentInfo,
  } = useCheckoutProductStore();

  const { formatPrice } = useLanguageStore();
  // const translations = useTranslations();

  // GHN Store for location data
  const {
    provinces,
    fetchProvinces,
    fetchDistricts,
    fetchWards,
  } = useGHNStore();

  // Fetch provinces on component mount
  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  useEffect(() => {
    const hasSuccessfulCOD = sessionStorage.getItem("lianne_cod_order_success");
    if (hasSuccessfulCOD && checkoutProducts.length === 0) {
      navigate("/checkout", { replace: true });
    }
  }, [checkoutProducts.length, navigate]);

  const [formData, setFormData] = useState({
    email: user?.email || "",
    phone: user?.phone || "",
    customer_id : user?.id || 0,
    isGuestCheckout: !user?.id,
    firstName: "",
    lastName: "",
    deliveryMethod: "shipment",
    shippingAddress: "",
    shippingWard: "",
    shippingWardId: null,
    shippingDistrict:"",
    shippingCity: "",
    shippingState: "",
    shippingZipCode: "",
    shippingCountry: "",
    paymentMethod: "cod", // Mặc định COD - PayPal và VNPay tạm thời vô hiệu hóa
    orderComments: "",
  });

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    setFormData((current) => ({
      ...current,
      email: current.email || user.email || "",
      phone: current.phone || user.phone || "",
      customer_id: user.id,
      isGuestCheckout: false,
    }));
  }, [user]);

  // Use custom hook for shipping location management
  const {
    shippingLocation,
    handleShippingProvinceChange,
    handleShippingDistrictChange,
    handleShippingWardChange,
  } = useShippingLocation(provinces, fetchDistricts, fetchWards, setFormData);
  const [shippingCost, setShippingCost] = useState(0);

  // Use custom hook for order submission
  const {
    isSubmitting,
    orderSuccess,
    handleSubmit,
  } = useOrderSubmission(
    checkoutProducts,
    subtotal,
    shippingCost,
    createOrderFromCheckout,
    updateOrderWithPaymentInfo
  );


  // Calculate shipping cost when shipping location data changes
  // useEffect(() => {
  //   const getShippingCost = async () => {
  //     try {
  //       if (
  //         shippingLocation.selectedDistrict &&
  //         shippingLocation.selectedWard &&
  //         formData.shippingAddress
  //       ) {
  //         const res = await calculateShippingCost();
  //         if (res.success) {
  //           setShippingCost(res.shipping_cost);
  //         }
  //       }
  //     } catch (error) {
  //       // Silent error handling for production
  //     }
  //   };

  //   getShippingCost();
  // }, [
  //   shippingLocation.selectedDistrict,
  //   shippingLocation.selectedWard,
  //   formData.shippingAddress,
  // ]);

  const total = subtotal + shippingCost;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  
  return (
    <div className={cx("wrapper")}>
      {/* Success Modal */}
      <SuccessModal orderSuccess={orderSuccess} />
      
      <div className={cx("header")}>
        <Link to="/checkout" className={cx("back-link")}>
          <ArrowLeft size={20} />
          BACK TO ORDER
        </Link>
      </div>

      <div className={cx("content")}>
        <div className={cx("form-section")}>
          <form>
            {/* Personal Information */}
            <PersonalInfoForm
              formData={formData}
              handleInputChange={handleInputChange}
            />

            {/* Shipping Details */}
            <ShippingDetailsForm
              formData={formData}
              handleInputChange={handleInputChange}
              shippingLocation={shippingLocation}
              provinces={provinces}
              handleShippingProvinceChange={handleShippingProvinceChange}
              handleShippingDistrictChange={handleShippingDistrictChange}
              handleShippingWardChange={handleShippingWardChange}
            />

            {/* Payment Method Selection */}
            <PaymentMethodSelector
              formData={formData}
              handleInputChange={handleInputChange}
            />
          </form>
        </div>

        {/* Order Summary */}
        <OrderSummary
          checkoutProducts={checkoutProducts}
          formatPrice={formatPrice}
          subtotal={subtotal}
          shippingCost={shippingCost}
          total={total}
          handleSubmit={() => handleSubmit(formData, shippingLocation)}
          isSubmitting={isSubmitting}
          orderSuccess={orderSuccess}
          formData={formData}
        />
      </div>
    </div>
  );
};

export default OrderInfoPage;
